import { MAX_SIZE_BYTES } from "../../utils/constants";
import {
  validateRequired,
  validateSSN,
  validateZipCode,
} from "../../utils/commonHandlers";
import { ConfirmationIcon, FailedIcon } from "../../assets/icons";
import { removeOpenedDoc } from "../../slice/MatterDetailsSlice";
import { buildAIPaneSearch, getAIPaneTab } from "../../utils/aiPaneUrl";

export async function validateDocument(doc) {
  try {

    // Check file size 
    if (doc?.size > MAX_SIZE_BYTES) {
      return { isValid: false, reason: 'size' };
    }

    // Step 1: Range request (first 8 KB)
    const partialRes = await fetch(doc?.s3_path, {
      headers: { Range: "bytes=0-8191" },
    });

    if (!partialRes.ok) {
      return { isValid: false, reason: "fetch_failed" };
    }

    const partialText = await partialRes.text();
    if (partialText.includes("/Encrypt")) {
      return { isValid: false, reason: "password_protected" };
    }

    // Step 2: Full fetch fallback
    const fullRes = await fetch(doc?.s3_path);
    if (!fullRes.ok) {
      return { isValid: false, reason: "fetch_failed" };
    }

    const fullText = await fullRes.text();
    if (fullText.includes("/Encrypt")) {
      return { isValid: false, reason: "password_protected" };
    }

    return { isValid: true };
  } catch (err) {
    console.error("PDF validation error:", err);
    return { isValid: false, reason: "validation_error" };
  }
}

// Form utility functions
export const createFieldValidations = () => ({
  veteranFirstName: (value) => validateRequired(value, 'First name'),
  veteranLastName: (value) => validateRequired(value, 'Last name'),
  fileNumber: validateSSN,
  zipCode: validateZipCode,
  source: (value) => validateRequired(value, 'Organization'),
  docType: (value) => validateRequired(value, 'Document type'),
  businessLine: (value) => value ? null : 'Benefit claim type is required.',
});

export const validateForm = (formData, fieldValidations) => {
  const newErrors = {};
  
 for (const [field, validationFn] of Object.entries(fieldValidations)) {
    const error = validationFn(formData[field]);
    if (error) {
      newErrors[field] = error;
    }
  };
  return newErrors;
};

export const areRequiredFieldsFilled = (formData) => {
  return (formData.veteranFirstName?.trim() || '') && 
         (formData.veteranLastName?.trim() || '') && 
         (formData.fileNumber?.trim() || '') && 
         (formData.zipCode?.trim() || '') && 
         (formData.source?.trim() || '') && 
         (formData.docType?.trim() || '') && 
         formData.businessLine;
};

export const getInitialFormData = () => ({
  veteranFirstName: "",
  veteranLastName: "",
  fileNumber: "",
  zipCode: "",
  source: "",
  docType: "",
  businessLine: "",
});

export const handleSSNInput = (e, handleInputChange) => {
  const value = e.target.value.replaceAll(/[^0-9-]/g, '');
  handleInputChange('fileNumber', value);
};

export const prepareFinalFormData = (formData, selectedDoc) => {
  const finalFormData = {
    ...formData,
    businessLine: formData.businessLine === "OTH" ? "CMP" : formData.businessLine
  };
  finalFormData.document_id = selectedDoc?.unique_id?.replace(/^D/, '');
  return finalFormData;
};

export const getFolderNameById = (folderId, breadcrumbs) => {
  if (!folderId || !Array.isArray(breadcrumbs)) return null;

  const folder = breadcrumbs.find(item => item.id === folderId && item.type === "folder");
  return folder ? folder.name : null;
};

export function getFolderConflictInfo(treeData, parentFolderId, folderNames) {
  if (!Array.isArray(treeData) || !Array.isArray(folderNames) || folderNames.length === 0) {
    return {
      conflictNames: [],
      conflictIds: [],
      siblingFolderNames: [],
    };
  }

  const siblings = getSiblings(treeData, parentFolderId);

  const siblingFolderNames = siblings
    .filter(node => node?.type === "folder" && typeof node?.title === "string")
    .map(node => node.title.trim());

  const conflicts = siblings
    .filter(node => node?.type === "folder" && typeof node?.title === "string")
    .filter(node => folderNames.includes(node.title.trim()));

  const conflictNames = conflicts.map(node => node.title.trim());
  const conflictIds = conflicts.map(node => node.key);

  return {
    conflictNames,
    conflictIds,
    siblingFolderNames,
  };
}


export function checkFileConflicts(treeData, parentFolderId, files) {
  if (!Array.isArray(treeData) || !Array.isArray(files) || files.length === 0) {
    return [];
  }

  const siblings = getSiblings(treeData, parentFolderId);
  const conflicts = [];

  for (const file of files) {
    const fileName = file?.name?.trim();
    if (!fileName) continue;

    const matchingNode = findMatchingNode(siblings, "document", fileName);
    if (matchingNode) {
      conflicts.push({
        name: fileName,
        id: matchingNode.key,
      });
    }
  }

  return conflicts;
}

export function renameFolderForKeepBoth(
  folderNames,        // array of folder names
  existingFolderNames, // array of sibling folder names
  files                // array of files
) {
  const folderNameSet = new Set(existingFolderNames.map((name) => name.trim()));
  const folderRenameMap = {};

  // Generate new names only for folders that conflict
  for (const originalFolderName of folderNames){
    let newFolderName = originalFolderName;
    let counter = 1;
    while (folderNameSet.has(newFolderName.trim())) {
      newFolderName = `${originalFolderName} (${counter})`;
      counter++;
    }
    folderNameSet.add(newFolderName.trim()); // prevent reuse in next iterations
    folderRenameMap[originalFolderName] = newFolderName;
  };

  // Process files
  const updatedFiles = files.map((file) => {
    let newWebkitRelativePath = file.webkitRelativePath;
    if (file.webkitRelativePath && file.webkitRelativePath.trim() !== "") {
      const pathParts = file.webkitRelativePath.split("/");
      if (pathParts.length > 0 && folderRenameMap[pathParts[0]]) {
        pathParts[0] = folderRenameMap[pathParts[0]];
        newWebkitRelativePath = pathParts.join("/");
      }
    }

    let newCustomRelativePath = file.customRelativePath;
    if (file.customRelativePath && file.customRelativePath.trim() !== "") {
      const customParts = file.customRelativePath.split("/");
      if (customParts.length > 0 && folderRenameMap[customParts[0]]) {
        customParts[0] = folderRenameMap[customParts[0]];
        newCustomRelativePath = customParts.join("/");
      }
    }

    return {
      file, // keep the original File object intact
      webkitRelativePath: newWebkitRelativePath,
      customRelativePath: newCustomRelativePath,
    };
  });

  return {
    folderRenameMap,
    updatedFiles,
  };
}

// Finds a node by ID recursively
export function findFolderById(nodes, id) {
  for (const node of nodes) {
    if (node?.key === id || node?.id === id) return node;
    if (Array.isArray(node?.children)) {
      const found = findFolderById(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

// Gets sibling nodes for a given parentFolderId
export function getSiblings(treeData, parentFolderId) {
  if (!Array.isArray(treeData)) return [];

  if (parentFolderId == null) {
    return treeData;
  } else {
    const parentFolder = findFolderById(treeData, parentFolderId);
    return Array.isArray(parentFolder?.children) ? parentFolder.children : [];
  }
}

// Finds a node matching type and name within siblings
export function findMatchingNode(siblings, type, name) {
  return siblings.find(
    (node) =>
      node?.type === type &&
      typeof node?.title === "string" &&
      node.title.trim() === name.trim()
  );
}

export function getConflictText(conflictType, conflictNames = []) {
  if (!Array.isArray(conflictNames)) conflictNames = [];

  if (conflictType === "folder") {
    if (conflictNames.length <= 2) {
      const names = conflictNames.join('", "');
      const folderLabel = conflictNames.length > 1 ? "s" : "";
      const existLabel = conflictNames.length > 1 ? "" : "s";
      return `Warning: Folder${folderLabel} "${names}" already exist${existLabel}`;
    } else {
      return "Warning: Folders with the same names already exist";
    }
  }

  if (conflictType === "file") {
    if (conflictNames.length > 2) {
      return "Some files already exist in this location. Proceeding will replace the existing file with the new one you are uploading.";
    } else {
      const names = conflictNames.join(", ");
      return `"${names}" already exists in this location. Proceeding will replace the existing file with the new one you are uploading.`;
    }
  }

  return "";
}

export const extractMatterAndFolderFromPath = (location) => {
  try {
    const pathname = location?.pathname || "";
    if (!pathname.includes("/matter")) {
      return { matterId: null, folderId: null };
    }

    const relativePath = pathname.split("/matter")[1] || "";
    const parts = relativePath.split("/").filter(Boolean);

    if (parts.length === 0) return { matterId: null, folderId: null };

    const matterId = parts[0]?.replace(/^F/, "") || null;

    if (parts.length === 1) {
      // only matterId present → folder is root
      return { matterId, folderId: "root" };
    }

    const lastSegment = parts.at(-1);

    if (lastSegment.startsWith("D")) {
      // inside document → folder null
      return { matterId, folderId: null };
    }

    if (lastSegment.startsWith("F")) {
      // inside folder → folder = last segment
      return { matterId, folderId: lastSegment.replace(/^F/, "") };
    }

    // fallback for any unexpected format
    return { matterId, folderId: "root" };
  } catch {
    return { matterId: null, folderId: null };
  }
};


// utils/tabUtils.js
export function reorderTabs(prevTabs, clickedTab, urlIds, dispatch, updateTabOrder) {
  const currentIndex = prevTabs.findIndex(t => t.id === clickedTab.id);
  if (currentIndex === -1) return prevTabs;

  const indexOfId1 = prevTabs.findIndex(t => t.id === "1");
  if (indexOfId1 === -1) return prevTabs;

  const newTabs = [...prevTabs];
  const [tabToMove] = newTabs.splice(currentIndex, 1);

  const insertIndex = indexOfId1 < currentIndex ? indexOfId1 + 1 : indexOfId1;

  newTabs.splice(insertIndex, 0, tabToMove);

  // Dispatch backend update
  dispatch(updateTabOrder({
    matter_id: urlIds[0],
    document_id: clickedTab.id.substring(1),
    new_position: insertIndex - 1
  }));

  return newTabs;
}


// utils/tabUtils.js

export function handleVisibleTabDropLogic({
  data,
  dropdownTabs,
  visibleTabs,
  targetIndex,
  MAX_VISIBLE_TABS
}) {
  if (data.type !== 'dropdown-tab') return { newTabs: null, activeTabId: null };

  const draggedTab = data.tab;

  // Remove from dropdown
  const remainingDropdownTabs = dropdownTabs.filter(t => t.id !== draggedTab.id);

  // Insert dragged tab into visible tabs at target position
  const newVisibleTabs = [...visibleTabs];
  newVisibleTabs.splice(targetIndex, 0, draggedTab);

  // If visible tabs exceed limit, move the last one to dropdown
  if (newVisibleTabs.length > MAX_VISIBLE_TABS) {
    const movedTab = newVisibleTabs.pop();
    remainingDropdownTabs.unshift(movedTab);
  }

  const newTabs = [...newVisibleTabs, ...remainingDropdownTabs];
  return {
    newTabs,
    activeTabId: draggedTab.id,
  };
}

export const getTabBackground = (activeTab, tabId) => {
    return activeTab === tabId || tabId === "0" ? "true" : "";
  };

// Shown when the server rejects a submission but gave no usable reason.
// Never claim a specific cause here — for weeks this said "Missing required
// field Document Type" for every failure, and staff chased the wrong problem.
export const VA_SUBMIT_GENERIC_FAILURE =
  "Veterans Affairs did not accept this file and no reason was returned. Please try again or contact admin.";

export const VA_FIX_QUEUED_MESSAGES = {
  page_size:
    "A copy with the oversized pages scaled down to letter size is being made and will be submitted to Veterans Affairs automatically — no further action needed. It appears in the matter next to the original as “(VA resized)”. You can check the upload status from Dashboard/Home screen.",
  file_size:
    "A compressed copy is being made and will be submitted to Veterans Affairs automatically — no further action needed. It appears in the matter next to the original as “(VA compressed)”. You can check the upload status from Dashboard/Home screen.",
};

/**
 * One plain sentence explaining why the submit call failed, taken from the
 * server's response where it gave one (422 validation, 400 field errors).
 */
export function describeVaSubmitError(error) {
  const data = error?.response?.data;
  if (!data) return VA_SUBMIT_GENERIC_FAILURE;
  if (typeof data === "string") return data;
  if (typeof data.error === "string" && data.error) return data.error;
  if (typeof data.detail === "string" && data.detail) return data.detail;
  // DRF serializer errors: { field: ["msg", ...], ... }
  const fieldErrors = Object.entries(data)
    .filter(([, v]) => Array.isArray(v) || typeof v === "string")
    .map(([field, v]) => `${field}: ${Array.isArray(v) ? v.join(" ") : v}`);
  return fieldErrors.length ? fieldErrors.join("; ") : VA_SUBMIT_GENERIC_FAILURE;
}

/**
 * Copy for the "Fix & Submit" offer the server's 422 unlocks. `data` is the
 * 422 body: { error, error_code, fixable }.
 */
export function getVaFixOfferCopy(data) {
  const reason = data?.error ? `${data.error}. ` : "";
  if (data?.error_code === "page_size") {
    return {
      title: "A page is too large for Veterans Affairs",
      message: `${reason}WHG Docs can make a copy of this file with the oversized pages scaled down to letter size and submit that instead. Smaller pages are left as they are, and your original file is not changed.`,
      confirmText: "Resize & Submit",
    };
  }
  return {
    title: "File is over the Veterans Affairs size limit",
    message: `${reason}WHG Docs can create a compressed copy in this matter and submit that copy instead. Your original file is not changed.`,
    confirmText: "Compress & Submit",
  };
}

export function getModalConfig({ type, closeHandler, message }) {
  const configs = {
    failure: {
      title: "File Failed Metadata Checks",
      message: message || VA_SUBMIT_GENERIC_FAILURE,
      icon: <FailedIcon />,
      variant: "failure",
    },
    fixQueued: {
      title: "Preparing a VA-ready copy",
      message: message || VA_FIX_QUEUED_MESSAGES.file_size,
      icon: <ConfirmationIcon />,
      variant: "confirmation",
    },
    internalFailure: {
      title: "File Failed Metadata Checks",
      message:
        "The selected PDF cannot be uploaded because it either exceeds the 100 MB file size limit or is password-protected. Please check the file and try again or contact admin.",
      icon: <FailedIcon />,
      variant: "failure",
    },
    success: {
      title: "Sent to the VA",
      message: "This file is on its way. Do not send it again.",
      icon: <ConfirmationIcon />,
      variant: "success",
      buttonText: "Got it",
    },
  };

  const modal = configs[type];
  if (!modal) throw new Error(`Unknown modal type: ${type}`);

  return {
    isOpen: true,
    buttonText: "Close",
    ...modal,
    onClose: closeHandler,
    onClick: closeHandler,
  };
}

export const handleSnackbar = (
  setdrawerError,
  setShowNotification,
  setisLoading,
  msg,
  success = false,
  stopLoading = true,
  options = {}
) => {
  const id = Date.now().toString();
  const res = {
    id,
    newMsg: msg,
    ...(success && { success: true }),
    ...(options.copyText && { copyText: options.copyText }),
    ...(options.copyLabel && { copyLabel: options.copyLabel }),
  };

  setdrawerError([res]);
  setShowNotification(true);
  if (stopLoading) {
    setisLoading(false);
  }
};

export const createHandleSnackbar = (setdrawerError, setShowNotification, setisLoading) => {
  return (msg, success = false, stopLoading = true) => {
    const id = Date.now().toString();
    const res = {
      id,
      newMsg: msg,
      ...(success && { success: true }),
    };

    setdrawerError([res]);
    setShowNotification(true);
  };
};

export const getESignErrorDetail = (err) => {
  const data = err?.response?.data || err?.data;
  if (typeof data === "string") return data;

  if (data && typeof data === "object") {
    if (typeof data.detail === "string") return data.detail;
    if (typeof data.error === "string") return data.error;
    if (typeof data.message === "string") return data.message;

    if (Array.isArray(data.errors) && data.errors.length) {
      return data.errors
        .map((item) => (typeof item === "string" ? item : item?.message || item?.error))
        .filter(Boolean)
        .join("; ");
    }

    try {
      return JSON.stringify(data);
    } catch {
      return "";
    }
  }

  return err?.message || "";
};

export const buildESignErrorToast = (err, fallback = "Unable to send request") => {
  const detail = getESignErrorDetail(err).trim();
  const summary = detail || fallback;
  const clipped = summary.length > 80 ? `${summary.slice(0, 77)}...` : summary;

  return {
    message: `E-signature failed: ${clipped}`,
    copyText: detail || fallback,
  };
};


export const updateTitleName = (setTitleName, idOrRecord, value, fileType = '') => {
  let id, name;

  if (typeof idOrRecord === "object") {
    // Case: record
    id = idOrRecord.unique_id;
    name = idOrRecord?.name ?? "";
  } else {
    // Case: id + value (from input)
    id = idOrRecord;
    // Store the full filename with extension
    name = value && fileType ? `${value}.${fileType}` : value;
  }

  setTitleName((prev) => ({
    ...prev,
    [id]: name,
  }));
};


  // Helper to reset editing states and loading
  export const resetEditingState = (setEditingKey, setEditingValue, setisLoading) => {
    setEditingKey(null);
    setEditingValue("");
    setisLoading(false);
  };

  export const handleSubmitESign = (payload, dispatch, initiateESign, sendForESign, setdrawerError, setShowNotification, setisLoading) => {
    payload.document_id = payload.selectedDoc.unique_id.substring(1);
  
    return dispatch(sendForESign(payload)) // ✅ return here
      .unwrap()
      .then((response) => {
        if (response?.data?.signature_request?.id) {
          return dispatch(initiateESign({ id: response?.data?.signature_request?.id }))
            .unwrap()
            .then((res) => {
              handleSnackbar(setdrawerError, setShowNotification, setisLoading, "E-Signature Sent Successfully", true);
              return { success: true }; // ✅ return to signal success
            })
            .catch((err) => {
              console.error("Error in initiating e-sign", err);
              const toast = buildESignErrorToast(err, "Unable to start signing workflow");
              handleSnackbar(
                setdrawerError,
                setShowNotification,
                setisLoading,
                toast.message,
                false,
                true,
                { copyText: toast.copyText, copyLabel: "Copy error" }
              );
              if (err && typeof err === "object") {
                err.__esignToastShown = true;
              }
              throw err;
            });
        } else {
          throw new Error("No signature request ID");
        }
      })
      .catch((err) => {
        console.error("Error in sending for e-sign", err);
        if (err?.__esignToastShown) {
          throw err;
        }
        const toast = buildESignErrorToast(err, "Unable to create signature request");
        handleSnackbar(
          setdrawerError,
          setShowNotification,
          setisLoading,
          toast.message,
          false,
          true,
          { copyText: toast.copyText, copyLabel: "Copy error" }
        );
        throw err;
      });
  };

  export const handlePdfValidation = async (formData, setVAFormData, setShowSendToVAModal, setConfirmSubmit) => {
    try {
      setVAFormData(formData);
      setShowSendToVAModal(false);
      setConfirmSubmit(true);
    } catch (error) {
      console.error('handlePdfValidation error:', error);
    }
  };

  // A second click can fire before React re-renders the disabled button.
  // One in-flight send per page; extra clicks are ignored.
  let vaSubmitInFlight = false;

  export const handleConfirmSubmit = async (setisLoading, sendVAFormData, VAFormData, setConfirmSubmit, setVAFormData, openModal, onFixable) => {
    if (vaSubmitInFlight) {
      return;
    }
    vaSubmitInFlight = true;
    setisLoading(true);
    try {
      await sendVAFormData(VAFormData);
      setConfirmSubmit(false);
      setVAFormData({}); // Clear form data after successful submission
      openModal("success");

    } catch (error) {
      console.error("Error submitting to VA:", error);
      setConfirmSubmit(false);
      const data = error?.response?.data;
      if (data?.fixable && typeof onFixable === "function") {
        // Oversized page / oversized file: offer the VA-ready copy path
        // instead of a dead end. Form data is kept for the follow-up call.
        onFixable(data);
        return;
      }
      openModal("failure", describeVaSubmitError(error));
    } finally {
      vaSubmitInFlight = false;
      setisLoading(false);
    }
  }

  // The user accepted the Resize/Compress & Submit offer: queue the
  // background fix-and-submit with the same veteran details.
  export const handleVaFixAndSubmit = async (setisLoading, compressAndSendVAFormData, VAFormData, offer, setVaFixOffer, setVAFormData, openModal) => {
    if (vaSubmitInFlight) {
      return;
    }
    vaSubmitInFlight = true;
    setisLoading(true);
    try {
      await compressAndSendVAFormData(VAFormData);
      setVaFixOffer(null);
      setVAFormData({});
      openModal("fixQueued", VA_FIX_QUEUED_MESSAGES[offer?.error_code] || VA_FIX_QUEUED_MESSAGES.file_size);
    } catch (error) {
      console.error("Error queueing VA fix-and-submit:", error);
      setVaFixOffer(null);
      openModal("failure", describeVaSubmitError(error));
    } finally {
      vaSubmitInFlight = false;
      setisLoading(false);
    }
  }

  export const handleGoBackToVa = (setConfirmSubmit, setShowSendToVAModal) => {
    setConfirmSubmit(false);
    setShowSendToVAModal(true);
  };
  
  export const handleShowVaCancelModal = (setConfirmCloseModal) => {
    setConfirmCloseModal(true)
  }

  export const handleConfirmVaClose = (setVAFormData, setConfirmCloseModal, setConfirmSubmit) => {
    setVAFormData({});
    setConfirmCloseModal(false)
    setConfirmSubmit(false)
  }

  export const handleGoBackToVA = (setConfirmCloseModal) => {
    setConfirmCloseModal(false)
  }

  export const handleReviewSubmit = ({selectedDoc, shareID, reviewActionType, actionComment, setConfirmCloseTab, setShowReviewActionModal, handleReviewAction, tabs, setActionComment, setReviewActionType}) => {
    const docUniqueId = selectedDoc?.unique_id;
    const tabIsOpen = tabs.some((tab) => tab.id === docUniqueId);

    if (tabIsOpen) {
      setConfirmCloseTab(true);
      setShowReviewActionModal(false);
    } else {
      handleReviewAction(selectedDoc, shareID, reviewActionType, actionComment);
      setActionComment("");
      setShowReviewActionModal(false);
      setReviewActionType(null);
    }
  };

  export const handleGoBackToAction = (setConfirmCloseTab, setShowReviewActionModal) => {
    setConfirmCloseTab(false);
    setShowReviewActionModal(true);
  };

  export const handleCloseActionModal = (setShowReviewActionModal, setReviewActionType, setActionComment) => {
    setShowReviewActionModal(false);
    setReviewActionType(null);
    setActionComment("");
  };


  export const handleCloseSendToVAModal = (setShowSendToVAModal, setVAFormData) => {
    setShowSendToVAModal(false);
    setVAFormData({}); // Clear the form data when modal is closed
  };

  export const handleConfirmReviewAction = ({selectedDoc, shareID, reviewActionType, actionComment, setConfirmCloseTab, setShowReviewActionModal, handleReviewAction, setActionComment, setReviewActionType}) => {
    handleReviewAction(selectedDoc, shareID, reviewActionType, actionComment);
    setConfirmCloseTab(false);
    setActionComment("");
    setReviewActionType(null);
    setShowReviewActionModal(false);
  };

  export const closeTabsByIds = (tabs, setTabs, idsToClose) => {
    if (Array.isArray(idsToClose) && Array.isArray(tabs)) {
      const updatedTabs = tabs.filter(tab => !idsToClose.includes(tab.id));
      setTabs(updatedTabs);
    }
  };

    // close modal
    export const closeConflictModal = (setConfirmOverwrite, setPendingUpload) => {
      setConfirmOverwrite(false);
      setPendingUpload(null);
    };


  export const updateTabState = (tabs, setTabs, id, newTitle, record) => {
    setTabs((prevTabs) =>
      prevTabs.map((tab) =>
        tab.id === id
          ? {
            ...tab,
            title: newTitle,
            content: {
              ...tab.content,
              props: {
                ...tab.content?.props,
                documentRecord: record,
              },
            },
          }
          : tab
      )
    );
  };

  export const handleShowActions = (dispatch, showActions, updateShowActions) => {
    dispatch(updateShowActions(!showActions))
  }

  export const handleDragEnter = (setDropTargetName, setIsDraggingOverRow, targetName) => {
    setDropTargetName(targetName);
    setIsDraggingOverRow(true);
  };

  export const handleConfirmOverwrite = ({pendingUpload, handleProcessFiles, folderConflictOption, treeData, getAllChildDocumentIdsByFolderId, tabs, setTabs, setConfirmOverwrite, setPendingUpload}) => {
    const opts = pendingUpload?.aiProcess ? { aiProcess: true } : undefined;
    if (pendingUpload?.conflictType === "file") {
       // Proceed with file overwrite
      closeTabsByIds(tabs, setTabs, pendingUpload?.conflictIds);
      handleProcessFiles(pendingUpload.files, pendingUpload.targetId, "file", opts);
    } else if (pendingUpload?.conflictType === "folder") {
      if (folderConflictOption === "replace") {
        // Proceed with folder overwrite for all conflicting folders
        const allChildDocIds = pendingUpload.conflictIds.flatMap((conflictId) =>
          getAllChildDocumentIdsByFolderId(treeData, conflictId)
        );
        closeTabsByIds(tabs, setTabs, allChildDocIds);
        handleProcessFiles(
          pendingUpload.files,
          pendingUpload.targetId,
          "folder",
          opts,
        );
      } else if (folderConflictOption === "keep") {
        // Rename each folder and proceed with upload
        const { updatedFiles } = renameFolderForKeepBoth(
          pendingUpload.folderNames,
          pendingUpload.siblingFolders,
          pendingUpload.files
        );
        handleProcessFiles(updatedFiles, pendingUpload.targetId, false, opts);
      }
    }
    closeConflictModal(setConfirmOverwrite, setPendingUpload);
  };

    // cancel
    export const handleCancelOverwrite = (setConfirmOverwrite, setPendingUpload) => {
      closeConflictModal(setConfirmOverwrite, setPendingUpload);
    };
  
    export const handleFolderConflictOption = (setFolderConflictOption, event) => {
      setFolderConflictOption(event.target.value);
    };
  
    // open modal (for both file + folder)
    export const openConflictModal = (setPendingUpload, setFolderConflictOption, setConfirmOverwrite, conflictType, payload) => {
      setPendingUpload({ conflictType, ...payload });
      if (conflictType === "folder") {
        setFolderConflictOption("replace"); // default
      }
      setConfirmOverwrite(true);
    };
      
  // Helper: Update breadcrumb if folder clicked
  export const updateBreadcrumb = (breadcrumb, setBreadcrumb, record) => {
    const foundIndex = breadcrumb.findIndex(
      (b) => b.id === record.unique_id && b.name === record.name
    );
    if (foundIndex === -1) {
        setBreadcrumb([...breadcrumb, { id: record.unique_id, name: record.name }]);
    } else {
        setBreadcrumb(breadcrumb.slice(0, foundIndex + 1));
    }
  };

  /**
   * Create breadcrumb from treeData for a given unique_id (document or folder)
   * @param {Array} treeData - The tree structure containing folders and documents
   * @param {Object} matterDetails - Matter details containing matter_displayName and id
   * @param {String} targetUniqueId - The unique_id to find (e.g., "D123" for document or "F456" for folder)
   * @returns {Array} Breadcrumb array in format: [{ id: 0, name: "Matters" }, { id: matterId, name: matterName }, ...folders..., { id: docId, name: docName }]
   */
  export const createBreadcrumbFromTreeData = (treeData, matterDetails, targetUniqueId) => {
    if (!treeData || !targetUniqueId) {
      return [{ id: 0, name: "Matters" }];
    }

    // Start with Matters
    const breadcrumb = [{ id: 0, name: "Matters" }];

    // Add matter if available
    if (matterDetails?.matter_displayName && matterDetails?.id) {
      breadcrumb.push({
        id: matterDetails.id,
        name: matterDetails.matter_displayName
      });
    }

    // Helper function to recursively search for the target item
    const findItemPath = (items, targetId, currentPath = []) => {
      if (!items || !Array.isArray(items)) {
        return null;
      }

      for (const item of items) {
        // Check if this item matches the target
        if (item.unique_id === targetId || item.key === targetId) {
          return [...currentPath, {
            id: item.unique_id || item.key || item.id,
            name: item.name || item.title,
            type: item.type || (targetId.startsWith('D') ? 'document' : 'folder'),
            url: item.relativePath || item.relative_path
          }];
        }

        // Check in documents array
        if (item.documents && Array.isArray(item.documents)) {
          for (const doc of item.documents) {
            if (doc.unique_id === targetId) {
              return [...currentPath, {
                id: item.unique_id || item.key || item.id,
                name: item.name || item.title,
                type: 'folder',
                url: item.relativePath || item.relative_path
              }, {
                id: doc.unique_id,
                name: doc.name,
                type: 'document',
                url: doc.relative_path
              }];
            }
          }
        }

        // Check in folders array
        if (item.folders && Array.isArray(item.folders)) {
          const folderPath = findItemPath(item.folders, targetId, [
            ...currentPath,
            {
              id: item.unique_id || item.key || item.id,
              name: item.name || item.title,
              type: 'folder',
              url: item.relativePath || item.relative_path
            }
          ]);
          if (folderPath) return folderPath;
        }

        // Check in subfolders (nested structure)
        if (item.subfolders && Array.isArray(item.subfolders)) {
          const subfolderPath = findItemPath(item.subfolders, targetId, [
            ...currentPath,
            {
              id: item.unique_id || item.key || item.id,
              name: item.name || item.title,
              type: 'folder',
              url: item.relativePath || item.relative_path
            }
          ]);
          if (subfolderPath) return subfolderPath;
        }

        // Check in children (converted tree structure)
        if (item.children && Array.isArray(item.children)) {
          // Skip empty children placeholder [{}]
          const validChildren = item.children.filter(child => child && Object.keys(child).length > 0);
          if (validChildren.length > 0) {
            const childPath = findItemPath(validChildren, targetId, [
              ...currentPath,
              {
                id: item.unique_id || item.key || item.id,
                name: item.name || item.title,
                type: item.type || 'folder',
                url: item.relativePath || item.relative_path
              }
            ]);
            if (childPath) return childPath;
          }
        }
      }

      return null;
    };

    // Search through treeData
    const path = findItemPath(treeData, targetUniqueId);

    if (path && path.length > 0) {
      // Filter out duplicates and add to breadcrumb
      const uniquePath = path.filter((item, index, self) => 
        index === self.findIndex(t => t.id === item.id)
      );
      breadcrumb.push(...uniquePath);
    }

    return breadcrumb;
  };


  // Helper: Fetch matter detail list with loading toggle and error handling
  export const fetchFolderDetails = ({setisLoading, dispatch, setExpandedKeys, setSelectedKeys, fetchMatterDetailList, id, currentPage, folderId}) => {
    dispatch(setExpandedKeys([folderId]));
    dispatch(setSelectedKeys([folderId]));
    return dispatch(fetchMatterDetailList({
      id,
      page: currentPage,
      folder: folderId,
    })).then((resp) => {
      setisLoading(false);
      return resp;
    }).catch(() => setisLoading(false));
  };


  export const fetchUpdatedMatterList = ({uniqueId, fetchMatterDetailList, id, currentPage, sort, sortBy, dispatch, setisLoading}) => {
    const matterDetailAction = uniqueId
      ? fetchMatterDetailList({
        id,
        page: currentPage,
        folder: uniqueId,
        sort_direction: sort,
        sort_by: sortBy,
      })
      : fetchMatterDetailList({
        id,
        page: currentPage,
        sort_direction: sort,
        sort_by: sortBy,
      });

    dispatch(matterDetailAction).then(() => setisLoading(false));
  };

  export const handlePageChange = (setCurrentPage, dispatch, setDocListingPage, page, setIsPaginationLoading) => {
    setCurrentPage(page);
    if (setIsPaginationLoading) {
      setIsPaginationLoading(true);
    }
  };

  export const removeRemindInProgressId = (setRemindInProgressIds, shareId) => {
    setRemindInProgressIds((prev) => prev.filter((id) => id !== shareId));
  };


  export const updateReviewerCanRemind = (setReviewers, shareId) => {
    setReviewers((prevData) =>
      prevData.map((item) =>
        item.id === shareId ? { ...item, can_remind: false } : item
      )
    );
  };


  export const handleRemindClick = ({reviewData, isModal, setRemindStatusModal, setRemindStatusDrawer, remindInProgressIds, setRemindInProgressIds, dispatch, remindReview, setReviewers}) => {
    const shareId = reviewData?.id;
    const setRemindStatus = isModal ? setRemindStatusModal : setRemindStatusDrawer;

    //To Prevent multiple clicks
    if (remindInProgressIds.includes(shareId)) return;
    setRemindInProgressIds((prev) => [...prev, shareId]);

    dispatch(remindReview(shareId))
      .unwrap()
      .then((response) => {
        if (response?.status === 200) {
          updateReviewerCanRemind(setReviewers, shareId);
          setRemindStatus({
            user:
              reviewData?.shared_with?.full_name ||
              reviewData?.shared_with?.username,
            success: true,
          });
        }
      })
      .catch((err) => {
        console.error("Error in reminding user", err);
        if (err?.status !== 401) {
          setRemindStatus({
            user:
              reviewData?.shared_with?.full_name ||
              reviewData?.shared_with?.username,
            success: false,
          });
        }
      })
      .finally(() => {
        removeRemindInProgressId(setRemindInProgressIds, shareId);
      });
  };

  export const handlePageSizeChange = (setCurrentPage, dispatch, setDocListingPageSize, pageSize) => {
    setCurrentPage(1);
    dispatch(setDocListingPageSize(pageSize));
};



export const updateTabsAfterRename = ({dispatch, fetchMatterDetail, id, activeTab, createUpdatedTab, matterDetailsTree, fetchUpdatedMatterDetails, setRenameEditFlag, titleName, breadcrumb, tabs, setTabs, setTitleName, response, docID, updateDocumentDetail}) => {
  dispatch(fetchMatterDetail(id)).then(() => {
    const renamedData = response.payload.data;
    const newName = renamedData.name;
    const pdf = {
      type: "document",
      record: { record: renamedData },
      unique_id: renamedData.unique_id,
      file_type: renamedData.file_type,
      name: renamedData.name,
      ...renamedData,
    };

    dispatch(updateDocumentDetail({ name: newName })); //Updates document detail with new name

    const updatedTabs = tabs.map((item) => {
      if (item.id === activeTab) {
        return createUpdatedTab({
          item,
          pdfRenameResponse: response,
          pdf,
          matterDetailsTree,
          fetchUpdatedMatterDetails,
          setRenameEditFlag,
          titleName,
          breadcrumb,
        });
      }
      return item;
    });
    setTabs(updatedTabs);
    setTitleName((prev) => ({
      ...prev,
      [docID]: renamedData.name.trim(),
    }));
  });
};



export const handleRenameResponse = ({setRenameEditFlag, setTitleName, fetchMatterDetailList, uniqueId, id, currentPage, sort, sortBy, dispatch, setisLoading, setdrawerError, setShowNotification, response, docID, oldName, activeTab, createUpdatedTab, matterDetailsTree, fetchUpdatedMatterDetails, titleName, breadcrumb, tabs, setTabs,fetchMatterDetail, updateDocumentDetail, setDocumentBreadcrumb, fetchBreadCrumbs, memoryDiagnostics}) => {
  setRenameEditFlag((prev) => ({ ...prev, [docID]: false }));

  if (response.error) {
    const msg =
      response.payload.status === 409
        ? response.payload.response.data.detail || "Failed to Save the File."
        : "Failed to Rename the file";
    handleSnackbar(setdrawerError, setShowNotification, setisLoading, msg);
    setTitleName((prev) => ({
      ...prev,
      [docID]: oldName,
    }));
    return;
  }

  if (response.payload?.status === 200) {
    updateTabsAfterRename({dispatch, fetchMatterDetail, id, activeTab, createUpdatedTab, matterDetailsTree, fetchUpdatedMatterDetails, setRenameEditFlag, titleName, breadcrumb, tabs, setTabs, setTitleName, response, docID, updateDocumentDetail});
    fetchUpdatedMatterList({uniqueId, fetchMatterDetailList, id, currentPage, sort, sortBy, dispatch, setisLoading});
    
    // Update breadcrumb directly after successful rename to show updated name
    // Check if the renamed document is currently active (in document view) and breadcrumb update is available
    if (setDocumentBreadcrumb && activeTab && activeTab.startsWith("D") && activeTab === docID && response.payload?.data?.name) {
      const newName = response.payload.data.name;
      // Update documentBreadcrumb by finding and updating the document's name
      setDocumentBreadcrumb((prevBreadcrumb) => {
        return prevBreadcrumb.map((item) => {
          // Find the document item by unique_id (with or without 'D' prefix)
          if (item.id === docID || item.id === activeTab) {
            return { ...item, name: newName };
          }
          return item;
        });
      });
    }
  }
};

export const handleDeleteObject = ({childDocIds, response, setdrawerError, setShowNotification, setisLoading, dispatch, fetchMatterDetail, fetchMatterDetailList, id, currentPage, sort, sortBy, folderId, tabs, setTabs}) => {
  const res = { id: Date.now().toString(), newMsg: response?.payload?.data?.message || "Deletion successful", success: true };
  setdrawerError([res]);
  setShowNotification(true);
  closeTabsByIds(tabs, setTabs, childDocIds)
  dispatch(fetchMatterDetail(id)); //This updates the tree
  dispatch(
    fetchMatterDetailList({
      id: id,
      page: currentPage,
      sort_direction: sort,
      sort_by: sortBy,
      folder: folderId
    })
  ).then((responseElseMatterDetail) => {
    if (responseElseMatterDetail.payload?.status === 200) {
      setisLoading(false);
    }
  });
}


export const handleDelete = ({deleteDocument, setShowDeleteModal, setisLoading, dispatch, deleteDoc, fetchMatterDetail, id, currentPage, sort, sortBy, folderId, tabs, setTabs, setdrawerError, setShowNotification, selectedKeys, getAllChildDocumentIdsByFolderId, deleteFolder, treeData, handleCloseDeletedTab, fetchMatterDetailList, setSelectedDoc}) => {
  setShowDeleteModal(false);
  setisLoading(true)
  const uniqueIdDelete = deleteDocument?.unique_id;
  const deleteId = uniqueIdDelete?.substring(1)
  if (deleteDocument?.type === "folder") {
     const childDocIds = getAllChildDocumentIdsByFolderId(treeData, uniqueIdDelete);
    dispatch(deleteFolder({ folderId: deleteId }))
      .then((response) => {
        if (response.payload?.status === 200) {
          handleDeleteObject({childDocIds, response, setdrawerError, setShowNotification, setisLoading, dispatch, fetchMatterDetail, fetchMatterDetailList, id, currentPage, sort, sortBy, folderId, tabs, setTabs});
          setisLoading(false);
        }
      }).catch((err) => {
        console.error("Unable to delete folder", err)
        setdrawerError([{ id: Date.now().toString(), newMsg: "Unable to delete folder", success: false }]);
        setShowNotification(true);
      })
      .finally(() => {
        setShowDeleteModal(false);
        setisLoading(false);
        setSelectedDoc(null)
      });
  } else {
 dispatch(deleteDoc({ docId: deleteId }))
      .then((response) => {
        if (response.payload?.status === 200) {
          const res = {
            id: Date.now().toString(),
            newMsg: response?.payload?.data?.message || "Deletion successful",
            success: true
          };
          setdrawerError([res]);
          setShowNotification(true);
          const tabIsOpen = tabs.some(tab => tab.id === uniqueIdDelete);
          if (tabIsOpen) { //if tab is open, it must be closed
            handleCloseDeletedTab(uniqueIdDelete)
            dispatch(fetchMatterDetail(id));
            const currentUniqueId = selectedKeys[0];
            const fetchListParams = {
              id,
              page: currentPage,
              sort_direction: sort,
              sort_by: sortBy,
              ...(currentUniqueId?.startsWith("F") && { folder: currentUniqueId }),
            };
            dispatch(fetchMatterDetailList(fetchListParams));
          } else {
            dispatch(fetchMatterDetail(id)); //update tree
            const currentUniqueId = selectedKeys[0]
            const fetchListParams = {
              id,
              page: currentPage,
              sort_direction: sort,
              sort_by: sortBy,
              ...(currentUniqueId?.startsWith("F") && { folder: currentUniqueId }),
            };
            dispatch(fetchMatterDetailList(fetchListParams))
          }
        }
      }).catch((err) => {
        console.error("Unable to delete document", err);
        setdrawerError([ { id: Date.now().toString(), newMsg: "Unable to delete document", success: false }]);
        setShowNotification(true);
      })
      .finally(() => {
        setShowDeleteModal(false);
        setisLoading(false);
        setSelectedDoc(null)
      });

  }
};


export const deleteDocFunc = ({
  deleteDocuId,
  dispatch,
  setDeleteDoc,
  setDeleteFolderId,
  setModalFolderHeader,
  setModalMessage,
  setShowFolderModal,
  getAllChildDocumentIdsByFolderId,
  treeData,
  activeTab,
  location,
  onChildDocIdsReady,
  tabs,
}) => {

  const folderId = deleteDocuId;
  if(folderId?.startsWith("F")){
    // Compute childDocIds, while treeData still contains the deleted folder
    const childDocIds = getAllChildDocumentIdsByFolderId(treeData, folderId);
    
    // Stored them so handleFolderModalConfirm can use them after treeData updates
    if (typeof onChildDocIdsReady === "function") {
      onChildDocIdsReady(childDocIds);
    }

    // Determine if the current user is affected by this deletion
    const isInsideDeletedFolder = location?.pathname?.includes(folderId);
    const activeTabIsInsideFolder = childDocIds.includes(activeTab);
    
    // check if ANY open tab belongs to the deleted folder
    const hasBackgroundTabsInsideFolder = tabs.some(
      (tab) => childDocIds.includes(tab.id) && tab.id !== activeTab
    );

    // Show modal if user is affected in any way
    if (!isInsideDeletedFolder && !activeTabIsInsideFolder && !hasBackgroundTabsInsideFolder) {
      dispatch(setDeleteDoc(null));
      return;
    }

    setDeleteFolderId(folderId);
    setModalFolderHeader("Some folders deleted");

    if (activeTab === "1" && isInsideDeletedFolder) {
      // User is directly viewing the deleted folder tab
      setModalMessage(
        "The folder you were viewing has been deleted. You'll be redirected to the matter root."
      );
    } else if (activeTabIsInsideFolder) {
      // User has an active tab that is a file/subfolder inside the deleted folder
      setModalMessage(
        "File Unavailable: The folder containing this file has been deleted. You can no longer view or edit this document."
      );
    } else {
      // User has background tabs from inside the deleted folder
      setModalMessage(
        "Some open items are no longer available because their parent folder has been deleted. These tabs will now be closed."
      );
    }
    setShowFolderModal(true);
  }
  dispatch(setDeleteDoc(null));
};


export const handleTabDragStart = (e, tabId, setDraggedTabId) => {
  // Only allow dragging for tabs with ID not equal to "0" and "1"
  if (tabId === "0" || tabId === "1") {
    e.preventDefault();
    return;
  }
  setDraggedTabId(tabId);
  e.dataTransfer.effectAllowed = "move";
  e.dataTransfer.setData("text/html", tabId);
};

export const handleTabDragOver = (e, tabId, setDragOverTabId) => {
  e.preventDefault();
  // Only allow drag over for tabs with ID not equal to "0" and "1"
  if (tabId === "0" || tabId === "1") return;
  setDragOverTabId(tabId);
  e.dataTransfer.dropEffect = "move";
};

export const handleTabDragEnter = (e, tabId, setDragOverTabId) => {
  e.preventDefault();
  if (tabId === "0" || tabId === "1") return;
  setDragOverTabId(tabId);
};

export const handleTabDragLeave = (e, tabId, dragOverTabId, setDragOverTabId) => {
  e.preventDefault();
  if (dragOverTabId === tabId) {
    setDragOverTabId(null);
  }
};

export const saveFolderRename = async ({
  ex,
  id,
  currentPage,
  editingKey,
  editingValue,
  setisLoading,
  setEditingKey,
  setEditingValue,
  handleSnackbar,
  resetEditingState,
  dispatch,
  renameFolders,
  fetchMatterDetail,
  fetchMatterDetailList,
}) => {
  try {
    setisLoading(true);
    
    const res = await dispatch(renameFolders({ id: editingKey.substring(1), name: editingValue }));

    if (res.payload?.status === 200) {
      await dispatch(fetchMatterDetail(id));

      const fetchList = ex.parent_id
        ? dispatch(fetchMatterDetailList({ id, page: currentPage, folder: "F" + ex.parent_id }))
        : dispatch(fetchMatterDetailList({ id, page: currentPage }));

      const resp = await fetchList.unwrap();

      if (resp?.status === 200) {
        setisLoading(false);
      }

      resetEditingState(setEditingKey, setEditingValue, setisLoading);
    } else {
      resetEditingState(setEditingKey, setEditingValue, setisLoading);

      const errMsg =
        res.payload.status === 409
          ? res?.payload?.data?.detail.replace("in root.", "")
          : res?.payload?.data?.error ||
            res?.payload?.data?.detail ||
            "Failed to Rename the folder";

      handleSnackbar(errMsg);
    }
  } catch (error) {
    console.error("Error renaming folder:", error);
    resetEditingState(setEditingKey, setEditingValue, setisLoading);
    handleSnackbar("Failed to Rename the folder");
  } finally {
    setisLoading(false);
  }
};


export const shouldUnlockDocument = (selectedID) =>
  selectedID ? selectedID.locked_by === Number(localStorage.getItem("user_id")) : false;

export const handleDocumentUnlock = async (dispatch, editPDFsunLock, fetchUpdatedMatterDetails, selectedID) => {
  try {
    const res = await dispatch(editPDFsunLock(selectedID.unique_id.substring(1)));
    if (res.payload?.status === 200) {
      await fetchUpdatedMatterDetails(); // temporary fallback until socket stabilizes
    }
  } catch (err) {
    console.error("Error in PDF unlock API:", err);
  }
};

export const handleRemoveOpenedDoc = async (dispatch, id, docId, setdrawerError, setShowNotification) => {
  const response = await dispatch(removeOpenedDoc({ matterId: id, docId }));

  if (![404, 200].includes(response.payload?.status)) {
    const msg = response?.payload?.message || "Failed to remove opened document";
    handleError(msg, setdrawerError, setShowNotification);
    throw new Error(msg);
  }
};

export const resetRenameFlag = (setRenameEditFlag, uniqueId) => {
  setRenameEditFlag((prev) => ({
    ...prev,
    [uniqueId]: false,
  }));
};

export const handleTabClosure = ({tabId,deleteTab,selectedTab,tabs,activeTab,setTabs,setEditingKey,setEditingValue,dispatch,deleteHighlightMatter,setActiveTab,navigate,location,restoreTab1Selection,setSelectedKeys,uniqueId}) => {
  const newTabs = tabs.filter((tab) => tab.id !== tabId);
  setTabs(newTabs);
  setEditingKey(null);
  setEditingValue("");

  if (tabId === activeTab) {
    handleActiveTabClosure({
      newTabs,
      deleteTab,
      selectedTab,
      tabs,
      navigate,
      location,
      restoreTab1Selection,
      setActiveTab,
      dispatch,
      setSelectedKeys,
      uniqueId
    }
    );
  }

  const matterId =
    selectedTab.content.props.documentRecord.matter_id ||
    selectedTab.content.props.documentRecord.matter;

  //dispatch(deleteHighlightMatter(matterId));
};

export const handleActiveTabClosure = ({
  newTabs,
  deleteTab,
  selectedTab,
  tabs,
  navigate,
  location,
  restoreTab1Selection,
  setActiveTab,
  dispatch,
  setSelectedKeys,
  uniqueId
}
) => {
  if (deleteTab) {
    restoreDefaultTabState(navigate, location, restoreTab1Selection, setActiveTab, dispatch, setSelectedKeys, uniqueId);
    return;
  }

  // Index of the tab that just got closed (still present in `tabs`,
  // which is the pre-filter list) — NOT tab "1"'s index. Using tab "1"'s
  // index here used to make every close land on the tab immediately
  // after the homepage tab, no matter which tab was actually closed.
  const closedTabIndex = tabs.findIndex((tab) => tab.id === selectedTab?.id);
  if (newTabs.length >= 1) {
    handleSwitchToPreviousTab(tabs, closedTabIndex, navigate, location, setActiveTab);
  } else {
    handleFallbackTab(newTabs, navigate, location, setActiveTab, dispatch, setSelectedKeys);
  }
};

// Helper that preserves the AI-pane query param across navigation. The
// legacy `?chatbox=true` flag is treated as `?ai=chat` on read so older
// links keep working; new navigations always write the canonical `?ai=`
// form. Exported under the legacy name `navigateWithChatbox` so existing
// callers (TreeComponentUtils, TabView callers) don't need to be renamed
// in the same PR.
export const navigateWithChatbox = (navigate, location, path) => {
  const tab = getAIPaneTab(location.search);
  if (tab) {
    navigate(`${path}${buildAIPaneSearch("", tab)}`, { replace: true });
  } else {
    navigate(path, { replace: true });
  }
};

export const restoreDefaultTabState = (navigate, location, restoreTab1Selection, setActiveTab, dispatch, setSelectedKeys, uniqueId) => {
  setActiveTab("1");
  const folderId = uniqueId || null;
  dispatch(setSelectedKeys([folderId]));
  restoreTab1Selection();
  navigateWithChatbox(navigate, location, `/matter${localStorage.getItem("relativePath")}`);
};

// Closing the active tab now activates whichever tab sits immediately to
// its left (the matter homepage tab "1" if nothing is further left),
// instead of always jumping back to the matter homepage. That old
// behavior also dropped whatever document "The Brief" panel was showing,
// since the Brief tracks the document in the URL — landing on the
// homepage meant no document, so the Brief went blank (Matthew,
// 2026-08-10).
export const handleSwitchToPreviousTab = (tabs, closedTabIndex, navigate, location, setActiveTab) => {
  const previousTab = closedTabIndex > 0 ? tabs[closedTabIndex - 1] : tabs[0];
  const savedPath = localStorage.getItem("relativePath");
  const relativePath = previousTab?.relativePath
    ? `/matter${previousTab.relativePath}`
    : `/matter${savedPath || ""}`;

  navigateWithChatbox(navigate, location, relativePath);
  setActiveTab(previousTab?.id || "1");
};

export const handleFallbackTab = (newTabs, navigate, location, setActiveTab, dispatch, setSelectedKeys) => {
  navigateWithChatbox(navigate, location, `/matter${localStorage.getItem("relativePath")}`);
  const lastTab = newTabs.at(-1);
  if (lastTab) {
    setActiveTab(lastTab.id);
    dispatch(setSelectedKeys([lastTab.key]));
  } else {
    setActiveTab("1");
  }
};

export const handleError = (message, setdrawerError, setShowNotification) => {
  const id = Date.now().toString();
  setdrawerError([{ id, newMsg: message }]);
  setShowNotification(true);
};


export const checkAndUpdateRelativePath = (currentRelativePath) => {
  if (!currentRelativePath) {
    return null;
  }

  // Extract root ID from current location path (first segment after /)
  const currentPathParts = currentRelativePath.split("/").find(Boolean);

  const currentRootId = currentPathParts;

  if (!currentRootId) {
    return null;
  }

  // Get relativePath from localStorage
  const storedRelativePath = localStorage.getItem("relativePath");
  
  if (!storedRelativePath) {
    // If no stored path, set it to current matter's root
    const rootPath = `/${currentRootId}`;
    localStorage.setItem("relativePath", rootPath);
    return rootPath;
  }

  // Extract root ID from stored path
  const storedPathParts = storedRelativePath.split("/").find(Boolean);
  const storedRootId = storedPathParts;

  // If root IDs don't match, update localStorage to current matter's root
  if (storedRootId !== currentRootId) {
    const rootPath = `/${currentRootId}`;
    localStorage.setItem("relativePath", rootPath);
    return rootPath;
  }

  // Root IDs match, return stored path
  return storedRelativePath;
};


export const handleBlur = (
  { titleName, setTitleName, setRenameEditFlag, setPendingRename, renameDocument, handleSave, docLoading },
  record,
  file
) => {
  if (!file) {
    handleSave(record);
    return;
  }

  const { unique_id, file_type, name } = record;
  const trimmed = titleName?.[unique_id]?.trim();

  //Empty → revert immediately
  if (!trimmed) {
    setTitleName(prev => ({ ...prev, [unique_id]: name }));
    setRenameEditFlag(prev => ({ ...prev, [unique_id]: false }));
    setPendingRename(null);
    return;
  }

  //Still loading → queue rename
  if (docLoading) {
    setPendingRename({
      docID: unique_id,
      type: file_type,
      oldName: name,
    });
    setRenameEditFlag(prev => ({ ...prev, [unique_id]: false }));
    return;
  }

  //Ready → rename immediately
  renameDocument({ key: "Enter" }, unique_id, file_type, name);
};

export const checkHighlightedItem = (nodes) => {
  for (const node of nodes) {
    if (node.is_highlighted === true) {
      console.log("datah", node.record.unique_id);
      return node.record.unique_id;
    }
    if (node.children && node.children.length > 0) {
      const result = checkHighlightedItem(node.children);
      if (result) return result;
    }
  }
};

export const getNameWithoutExtension = (filename = "") => {
  const lastDot = filename?.lastIndexOf(".");
  return lastDot === -1 ? filename: filename?.substring(0, lastDot);
};

export const getExtension = (filename = "") => {
  const lastDot = filename?.lastIndexOf(".");
  return lastDot === -1 ? "" : filename?.substring(lastDot);

};